import { useState } from "react";
import { Card, Group, Stack, Text, Badge, Button, Avatar, Flex, Box, ActionIcon, Anchor, Tooltip } from "@mantine/core";
import {
  IconBookmark,
  IconMapPin,
  IconBuilding,
  IconRosetteDiscountCheckFilled,
  IconEye,
  IconPhone,
  IconMail,
  IconWorld,
  IconBrandLinkedin,
  IconExternalLink,
  IconFileText,
} from "@tabler/icons-react";
import { useAuth } from "../../hooks/useAuth";
import { useUser } from "@/context/UserContext";
import { useDisclosure } from "@mantine/hooks";
import ShortlistModal from "../shared/UserSignupModal";
import { Company } from "@gt/prisma";
import { getIndustryLabel } from "./constants/general.const";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";
import ExpressInterestModal, { ExpressInterestFormData } from "@/components/shared/ExpressInterestModal";

export interface CompanyWithUI extends Company {
  isShortlisted?: boolean;
  hasExistingRequest?: boolean;
}

export interface CompanyCardHorizontalProps {
  company: CompanyWithUI;
  onShortlistClick: (companyId: string, isShortlisted: boolean) => void;
  onViewDetails: (companyId: string) => void;
  showShortlistButton?: boolean;
  isLoading?: boolean;
  isShortlist?: boolean;
  isCollaborationList?: boolean;
  ndaStatus?: {
    currentUserSigned: boolean;
    bothSigned: boolean;
  };
  onSignNda?: (companyId: string) => void;
}

export default function CompanyCardHorizontal({
  company,
  onShortlistClick,
  onViewDetails,
  showShortlistButton = true,
  isShortlist = false,
  isCollaborationList = false,
  ndaStatus,
  onSignNda,
}: CompanyCardHorizontalProps) {
  const { isAuthenticated } = useAuth();
  const { userDetails } = useUser();
  const [modalOpened, setModalOpened] = useState(false);
  const [expressInterestModalOpened, { open: openExpressInterestModal, close: closeExpressInterestModal }] =
    useDisclosure(false);
  const [requestLoading, setRequestLoading] = useState(false);

  const handleConfirmExpressInterest = async (data: ExpressInterestFormData) => {
    setRequestLoading(true);
    try {
      await trpc.collaboration.request.mutate({
        targetCompanyId: company.id,
        requesterRole: data.requesterRole,
        expectedPartnerRole: data.expectedPartnerRole,
      });
      successNotification({
        title: "Success",
        message: "Your interest has been expressed to the company",
      });
      closeExpressInterestModal();
    } catch (error) {
      handleTrpcError(error);
      closeExpressInterestModal();
    } finally {
      setRequestLoading(false);
    }
  };

  const handleExpressInterestClick = () => {
    if (!isAuthenticated) {
      setModalOpened(true);
      return;
    }
    openExpressInterestModal();
  };

  const handleShortlistClick = () => {
    if (!isAuthenticated) {
      setModalOpened(true);
      return;
    }
    onShortlistClick(company.id, !company.isShortlisted);
  };

  const handleViewDetails = () => {
    onViewDetails(company.id);
  };

  // Use presigned URL from API response if available (for collaboration list)
  const logoUrl = isCollaborationList ? (company as any).logoPresignedUrl : null;

  return (
    <>
      <Card p="lg" radius="lg" withBorder>
        <Flex gap={{ base: "sm", sm: "md" }} direction={{ base: "column", sm: "row" }} align="stretch">
          {/* Avatar - Desktop only */}
          <Avatar size="lg" radius="md" variant="light" color="primary" visibleFrom="sm" src={logoUrl}>
            <IconBuilding size={24} />
          </Avatar>

          {/* Content */}
          <Stack gap="sm" style={{ flex: 1 }}>
            <Flex justify="space-between" align="center">
              <Group gap="sm" align="center">
                <Anchor
                  onClick={handleViewDetails}
                  td="none"
                  c="inherit"
                  styles={{
                    root: {
                      "&:hover": {
                        textDecoration: "underline",
                        cursor: "pointer",
                      },
                    },
                  }}
                >
                  <Text fw={400} fz={{ base: "md", sm: "lg" }}>
                    {isCollaborationList
                      ? (company.legalName ?? `${company.gtid}`)
                      : `${company.gtid}`}
                  </Text>
                </Anchor>
                {company.status === "VERIFIED" && (
                  <IconRosetteDiscountCheckFilled size={18} style={{ color: "var(--mantine-color-primary-6)" }} />
                )}
              </Group>

              {/* Mobile Actions - Only show heart if NOT in shortlist page */}
              {showShortlistButton && !isShortlist && (
                <Box hiddenFrom="sm">
                  <Button
                    variant="outline"
                    color={company.isShortlisted ? "yellow" : "gray"}
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShortlistClick();
                    }}
                    px="xs"
                    styles={{ root: { borderColor: company.isShortlisted ? "#FEE685" : undefined } }}
                  >
                    <IconBookmark size={18} fill={company.isShortlisted ? "currentColor" : "none"} />
                  </Button>
                </Box>
              )}
              {/* Mobile Actions - Shortlist Page remove button */}
              {showShortlistButton && isShortlist && (
                <Box hiddenFrom="sm">
                  <Button
                    variant="outline"
                    color="yellow"
                    size="xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShortlistClick();
                    }}
                    px="xs"
                    styles={{ root: { borderColor: "#FEE685" } }}
                  >
                    <IconBookmark size={18} fill="currentColor" />
                  </Button>
                </Box>
              )}
            </Flex>

            <Group gap="md" wrap="wrap">
              {isCollaborationList ? (
                <>
                  {((company as any).headquartersCity || company.headquartersCountry) && (
                    <Group gap={4}>
                      <IconMapPin size={14} style={{ color: "var(--mantine-color-slate-6)" }} />
                      <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                        {(company as any).headquartersCity && company.headquartersCountry
                          ? `${(company as any).headquartersCity}, ${company.headquartersCountry}`
                          : company.headquartersCountry || (company as any).headquartersCity}
                      </Text>
                    </Group>
                  )}
                  {((company as any).phoneCountryCode || (company as any).phoneNumber) && (
                    <Group gap={4}>
                      <IconPhone size={14} style={{ color: "var(--mantine-color-slate-6)" }} />
                      <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                        {(company as any).phoneCountryCode && (company as any).phoneNumber
                          ? `${(company as any).phoneCountryCode} ${(company as any).phoneNumber}`
                          : (company as any).phoneNumber || "N/A"}
                      </Text>
                    </Group>
                  )}
                  {(company as any).email && (
                    <Group gap={4}>
                      <IconMail size={14} style={{ color: "var(--mantine-color-slate-6)" }} />
                      <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                        {(company as any).email}
                      </Text>
                    </Group>
                  )}
                </>
              ) : (
                <>
                  <Group gap={4}>
                    <IconBuilding size={14} style={{ color: "var(--mantine-color-slate-6)" }} />
                    {Array.isArray(company.industryCategory) && company.industryCategory.length > 0 ? (
                      <Group gap={4}>
                        <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                          {getIndustryLabel(company.industryCategory[0])}
                        </Text>
                        {company.industryCategory.length > 1 && (
                          <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                            +{company.industryCategory.length - 1}
                          </Text>
                        )}
                      </Group>
                    ) : (
                      <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                        N/A
                      </Text>
                    )}
                  </Group>
                  {company.headquartersCountry && (
                    <Group gap={4}>
                      <IconMapPin size={14} style={{ color: "var(--mantine-color-slate-6)" }} />
                      <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                        {company.headquartersCountry}
                      </Text>
                    </Group>
                  )}
                </>
              )}
            </Group>

            {isCollaborationList ? (
              <Group gap="md" wrap="wrap">
                {(company as any).website && (
                  <Anchor
                    href={(company as any).website}
                    target="_blank"
                    td="none"
                    styles={{
                      root: {
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      },
                    }}
                  >
                    <Group gap={4}>
                      <IconWorld size={16} color="var(--mantine-color-primary-6)" />
                      <Text size="sm" c="primary.6">
                        Website
                      </Text>
                      <IconExternalLink size={12} color="var(--mantine-color-primary-6)" />
                    </Group>
                  </Anchor>
                )}
                {(company as any).linkedinUrl && (
                  <Anchor
                    href={(company as any).linkedinUrl}
                    target="_blank"
                    td="none"
                    styles={{
                      root: {
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      },
                    }}
                  >
                    <Group gap={4}>
                      <IconBrandLinkedin size={14} color="var(--mantine-color-primary-6)" />
                      <Text size="sm" c="primary.6">
                        LinkedIn
                      </Text>
                      <IconExternalLink size={12} color="var(--mantine-color-primary-6)" />
                    </Group>
                  </Anchor>
                )}
              </Group>
            ) : (
              company.coreCapabilities &&
              company.coreCapabilities.length > 0 && (
                <Group gap="xs">
                  {company.coreCapabilities.slice(0, 3).map((capability, index) => (
                    <Badge
                      key={index}
                      variant="light"
                      color="slate"
                      size="lg"
                      radius="md"
                      fw={400}
                      c="black"
                      styles={{ root: { textTransform: "none" } }}
                    >
                      {capability}
                    </Badge>
                  ))}
                  {company.coreCapabilities.length > 3 && (
                    <Badge
                      variant="light"
                      color="slate.6"
                      size="lg"
                      radius="md"
                      fw={400}
                      c="slate.6"
                      styles={{ root: { textTransform: "none" } }}
                    >
                      +{company.coreCapabilities.length - 3} more
                    </Badge>
                  )}
                </Group>
              )
            )}

            {/* Mobile Actions Full Width */}
            <Box hiddenFrom="sm" mt="sm">
              <Flex gap="sm" direction={{ base: "column", xs: "row" }}>
                {isShortlist && !company.hasExistingRequest ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      radius="md"
                      fullWidth
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails();
                      }}
                    >
                      View Profile
                    </Button>
                    <Button
                      size="sm"
                      radius="md"
                      color="primary"
                      fullWidth
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExpressInterestClick();
                      }}
                    >
                      Express Interest
                    </Button>
                  </>
                ) : isCollaborationList && ndaStatus ? (
                  <>
                    {!ndaStatus.currentUserSigned && onSignNda && (
                      <Button
                        variant="filled"
                        color="primary"
                        size="sm"
                        radius="md"
                        fullWidth
                        leftSection={<IconFileText size={16} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSignNda(company.id);
                        }}
                      >
                        Sign NDA
                      </Button>
                    )}
                    {ndaStatus.bothSigned ? (
                      <Button
                        variant="outline"
                        size="sm"
                        radius="md"
                        fullWidth
                        leftSection={<IconEye size={16} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails();
                        }}
                      >
                        Open Workspace
                      </Button>
                    ) : (
                      <Tooltip
                        label={
                          ndaStatus.currentUserSigned
                            ? "Waiting for them to sign NDA"
                            : "Both parties must sign NDA first"
                        }
                        withArrow
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          radius="md"
                          fullWidth
                          leftSection={<IconEye size={16} />}
                          disabled
                        >
                          Open Workspace
                        </Button>
                      </Tooltip>
                    )}
                  </>
                ) : (
                  <Button
                    variant="outline"
                    color="primary"
                    size="sm"
                    radius="md"
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails();
                    }}
                  >
                    View Details
                  </Button>
                )}
              </Flex>
            </Box>
          </Stack>

          {/* Desktop Right Side Actions */}
          <Box visibleFrom="sm">
            {isShortlist && !company.hasExistingRequest ? (
              <Stack align="flex-end" justify="space-between" gap="md" style={{ minHeight: "100%" }}>
                {/* Remove button (Heart) at the top */}
                {showShortlistButton && (
                  <ActionIcon
                    variant="outline"
                    color="yellow"
                    size="lg"
                    radius="md"
                    style={{ borderColor: "#FEE685" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShortlistClick();
                    }}
                  >
                    <IconBookmark size={18} fill="currentColor" />
                  </ActionIcon>
                )}

                {/* Action Buttons below */}
                <Group gap="sm">
                  <Button
                    variant="default"
                    size="sm"
                    radius="md"
                    leftSection={<IconEye size={16} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails();
                    }}
                  >
                    View Profile
                  </Button>
                  <Button
                    variant="filled"
                    color="primary"
                    size="sm"
                    radius="md"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExpressInterestClick();
                    }}
                  >
                    Express Interest
                  </Button>
                </Group>
              </Stack>
            ) : isCollaborationList && ndaStatus ? (
              <Group gap="sm" align="center">
                {!ndaStatus.currentUserSigned && onSignNda && (
                  <Button
                    variant="filled"
                    color="primary"
                    size="sm"
                    radius="md"
                    leftSection={<IconFileText size={16} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSignNda(company.id);
                    }}
                  >
                    Sign NDA
                  </Button>
                )}
                {ndaStatus.bothSigned ? (
                  <Button
                    variant="outline"
                    size="sm"
                    radius="md"
                    leftSection={<IconEye size={16} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails();
                    }}
                  >
                    Open Workspace
                  </Button>
                ) : (
                  <Tooltip
                    label={
                      ndaStatus.currentUserSigned ? "Waiting for them to sign NDA" : "Both parties must sign NDA first"
                    }
                    withArrow
                  >
                    <Button variant="outline" size="sm" radius="md" leftSection={<IconEye size={16} />} disabled>
                      Open Workspace
                    </Button>
                  </Tooltip>
                )}
              </Group>
            ) : (
              <Group gap="sm" align="center">
                {showShortlistButton && (
                  <Button
                    variant="outline"
                    color={company.isShortlisted ? "yellow" : "gray"}
                    size="sm"
                    radius="md"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShortlistClick();
                    }}
                    px="xs"
                    styles={{ root: { borderColor: company.isShortlisted ? "#FEE685" : undefined } }}
                  >
                    <IconBookmark size={18} fill={company.isShortlisted ? "currentColor" : "none"} />
                  </Button>
                )}
                <Button
                  variant="outline"
                  color="primary"
                  size="sm"
                  radius="md"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetails();
                  }}
                >
                  View Details
                </Button>
              </Group>
            )}
          </Box>
        </Flex>
      </Card>

      <ShortlistModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        companyName={company.gtid ? `${company.gtid}` : (company.legalName ?? "this company")}
      />
      <ExpressInterestModal
        opened={expressInterestModalOpened}
        onClose={closeExpressInterestModal}
        onConfirm={handleConfirmExpressInterest}
        loading={requestLoading}
        userWhoAmI={(userDetails?.company?.whoAmI as string[]) || []}
        targetCollaborationIntent={(company.collaborationIntent as string[]) || []}
      />
    </>
  );
}
